import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { apiSlice } from "../apiSlice";
import { dateFormatter } from "../../utils/dateFormatter";

/* ---------------- ADAPTER ---------------- */

const couponsListAdapter = createEntityAdapter({
  selectId: (coupon) => coupon.id,
});

const initialState = couponsListAdapter.getInitialState();

/* ---------------- API SLICE (QUERIES ONLY) ---------------- */

export const couponsListSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ✅ Fetch coupons list (SEARCH ONLY)
    getCouponsList: builder.query({
      query: ({ search } = {}) => {
        if (!search) return "coupons";
        return `coupons?search=${encodeURIComponent(search)}`;
      },

      transformResponse: (responseData) => {
        const coupons =
          responseData.data?.map((coupon) => ({
            ...coupon,

            // backend sends milliseconds
            start_date:
              coupon.start_date !== null && coupon.start_date !== undefined
                ? dateFormatter(coupon.start_date)
                : "N/A",

            end_date:
              coupon.end_date !== null && coupon.end_date !== undefined
                ? dateFormatter(coupon.end_date)
                : "N/A",
          })) ?? [];

        return couponsListAdapter.setAll(initialState, coupons);
      },

      providesTags: (result) =>
        result
          ? [
              { type: "coupons", id: "LIST" },
              ...result.ids.map((id) => ({ type: "coupons", id })),
            ]
          : [{ type: "coupons", id: "LIST" }],
    }),

    // ✅ Fetch coupon by ID
    getCouponById: builder.query({
      query: (id) => `coupons/${id}`,
      transformResponse: (res) => {
        const coupon = res.data;

        return {
          ...coupon,

          // backend sends milliseconds
          start_date:
            coupon.start_date !== null && coupon.start_date !== undefined
              ? dateFormatter(coupon.start_date)
              : "N/A",

          end_date:
            coupon.end_date !== null && coupon.end_date !== undefined
              ? dateFormatter(coupon.end_date)
              : "N/A",
        };
      },
      providesTags: (r, e, id) => [{ type: "coupons", id }],
    }),
  }),
});

/* ---------------- EXPORT HOOKS ---------------- */

export const { useGetCouponsListQuery, useGetCouponByIdQuery } =
  couponsListSlice;
const selectCouponSearch = (state) => state.couponFilter.search;

const selectCouponsListResult = createSelector(
  [selectCouponSearch, (state) => state],
  (search, state) =>
    couponsListSlice.endpoints.getCouponsList.select({ search })(state)
);

const selectCouponsListData = createSelector(
  [selectCouponsListResult],
  (result) => result?.data ?? initialState
);

export const {
  selectAll: selectCoupons,
  selectById: selectCouponById,
  selectIds: selectCouponIds,
} = couponsListAdapter.getSelectors((state) => selectCouponsListData(state));
